// /Users/pramod/Desktop/school-erp/frontend/src/Head-Of-Institution/LiveMonitoring/components/LiveSessionCard.jsx

import React, { useState } from 'react';
import { 
  User, 
  MapPin, 
  Clock, 
  BookOpen, 
  AlertCircle, 
  Layers, 
  Users as UsersIcon, 
  Maximize2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const LiveSessionCard = ({ session, onViewMore, index }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    className,
    subject,
    faculty,
    timeSlot,
    room,
    batch,
    isOverride,
    isBreak,
    isCustom,
    timeRemaining,
    electiveGroup,
    isElectiveSession,
    isLanguageGroup,
    electiveGroupName,
    sessionCategory
  } = session;

  const getFacultyName = () => {
    if (faculty?.name) return faculty.name;
    if (faculty?.facultyName) return faculty.facultyName;
    return null;
  };

  const getSubjectName = () => {
    if (subject?.name) return subject.name;
    if (subject?.subjectName) return subject.subjectName;
    return null;
  };

  const facultyName = getFacultyName();
  const subjectName = getSubjectName();
  const isGroupSession = isElectiveSession && electiveGroup && !facultyName;

  const getGroupDisplayName = (group) => {
    return group?.name || group?.label || 'Group';
  };

  const getSubGroupDisplayName = (subGroup, idx) => {
    if (subGroup?.subGroupName) return subGroup.subGroupName;
    if (subGroup?.name) return subGroup.name;
    return `Group ${idx + 1}`;
  };

  const getTotalFaculties = (group) => {
    if (!group?.subjects) return 0;
    return group.subjects.reduce((acc, s) => acc + (s.subGroups?.length || 0), 0);
  };

  const getTotalSubjects = (group) => {
    if (!group?.subjects) return 0;
    return group.subjects.length;
  };

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const hasMoreSubjects = (group) => {
    if (!group?.subjects) return false;
    return group.subjects.length > 2;
  };

  const hasMoreSubGroups = (group) => {
    if (!group?.subjects) return false;
    return group.subjects.some(s => s.subGroups && s.subGroups.length > 1);
  };

  return (
    <div className={`rounded-2xl p-4 border transition-all duration-200 bg-white ${
      isOverride ? 'border-amber-400 shadow-xs bg-amber-50/20' : 'border-green-200 hover:border-green-300 hover:shadow-sm'
    }`}>
      <div className="flex flex-col gap-3">
        
        {/* Card Header Top Row Component Elements */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
              <h4 className="font-bold text-gray-900 text-sm sm:text-base tracking-tight truncate">{className}</h4>
              
              {isOverride && (
                <span className="px-2 py-0.2 bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wider rounded border border-amber-200 flex items-center gap-0.5">
                  <AlertCircle className="w-2.5 h-2.5" /> Adjusted
                </span>
              )}
              {isBreak && (
                <span className="px-2 py-0.2 bg-gray-100 text-gray-600 text-[9px] font-bold uppercase tracking-wider rounded border border-gray-200">Interval</span>
              )}
              {isCustom && (
                <span className="px-2 py-0.2 bg-purple-100 text-purple-700 text-[9px] font-bold uppercase tracking-wider rounded border border-purple-200">Custom</span>
              )}
              {isElectiveSession && (
                <span className="px-2 py-0.2 bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider rounded border border-emerald-200">
                  {isLanguageGroup ? 'Language' : 'Elective'}
                </span>
              )}
            </div>

            {/* Time slot indicator tags */}
            <div className="flex items-center gap-2 text-xs text-gray-500 font-mono">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="font-semibold text-gray-700">{timeSlot?.start} - {timeSlot?.end}</span>
            </div>
          </div>

          {/* Action Trigger Anchors details panel */}
          {onViewMore && (
            <button
              type="button"
              onClick={() => onViewMore(session)}
              className="px-2.5 py-1.5 bg-gray-50 text-indigo-600 border border-gray-200 rounded-xl text-xs font-bold hover:bg-indigo-50 active:scale-95 transition-all flex items-center gap-1 flex-shrink-0"
            >
              <Maximize2 className="w-3 h-3" />
              Details
            </button>
          )}
        </div>

        {/* Core details layout grid mapping maps fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-600 pt-1.5 border-t border-gray-100">
          {subjectName && (
            <div className="flex items-center gap-1.5 min-w-0">
              <BookOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-semibold text-gray-800 truncate">
                {subjectName}
                {subject?.code && <span className="font-normal text-gray-400 text-[10px] ml-1">({subject.code})</span>}
              </span>
            </div>
          )}
          
          {isGroupSession && !subjectName && electiveGroupName && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Layers className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="font-semibold text-gray-800 truncate">
                {electiveGroupName} <span className="font-normal text-gray-400 text-[10px]">({isLanguageGroup ? 'Language' : 'Elective'} Block)</span>
              </span>
            </div>
          )}
          
          {!isGroupSession && facultyName && (
            <div className="flex items-center gap-1.5 min-w-0">
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate font-medium">{facultyName}</span>
            </div>
          )}
          
          {isGroupSession && electiveGroup && (
            <div className="flex items-center gap-1.5 min-w-0">
              <UsersIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate font-medium text-gray-500">
                {getTotalFaculties(electiveGroup)} Teachers • {getTotalSubjects(electiveGroup)} Subjects
              </span>
            </div>
          )}
          
          {room && (
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200/60 text-gray-700">{room}</span>
            </div>
          )}

          {batch && (
            <div className="flex items-center gap-1.5">
              <UsersIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="bg-gray-100 text-gray-600 font-medium px-2 py-0.5 rounded-md text-[10px]">{batch.name}</span>
            </div>
          )}
        </div>

        {/* Group list details mapping preview */}
        {isGroupSession && electiveGroup && electiveGroup.subjects && (
          <div className="pt-0.5">
            <div className="flex flex-wrap gap-1">
              {electiveGroup.subjects.slice(0, 2).map((sub, idx) => (
                <span key={idx} className="text-[11px] text-gray-600 bg-gray-50 px-2 py-0.5 rounded-lg border border-gray-200">
                  {sub.subjectName || 'Subject'}
                </span>
              ))}
              {electiveGroup.subjects.length > 2 && (
                <span className="text-[11px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-lg border border-gray-200 font-medium">
                  +{electiveGroup.subjects.length - 2} more
                </span>
              )}
            </div>
            
            {(hasMoreSubjects(electiveGroup) || hasMoreSubGroups(electiveGroup)) && (
              <button
                type="button"
                onClick={toggleExpanded}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-0.5 select-none"
              >
                {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Hide Subgroups</> : <><ChevronDown className="w-3.5 h-3.5" /> View Subgroups</>}
              </button>
            )}
          </div>
        )}

        {/* Extended drop details grouping content elements */}
        {expanded && isGroupSession && electiveGroup && electiveGroup.subjects && (
          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2 animate-fade-in text-xs">
            <div className="flex items-center gap-1.5 flex-wrap font-semibold">
              <Layers className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-gray-700">{getGroupDisplayName(electiveGroup)}</span>
              <span className={`px-1.5 py-0.2 rounded text-[9px] uppercase tracking-wide border ${
                electiveGroup.category === 'language' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-purple-50 text-purple-700 border-purple-100'
              }`}>
                {electiveGroup.category || 'Elective'}
              </span>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto pl-1 divide-y divide-gray-200/60">
              {electiveGroup.subjects.map((sub, idx) => (
                <div key={idx} className="pt-2 first:pt-0 pb-1">
                  <p className="font-bold text-gray-800">{sub.subjectName}</p>
                  {sub.subGroups?.map((sg, sgIdx) => (
                    <div key={sgIdx} className="pl-2 border-l border-indigo-200 text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                      <span className="font-semibold text-gray-600">{getSubGroupDisplayName(sg, sgIdx)}</span>
                      {sg.facultyName && <span>• Teacher: {sg.facultyName}</span>}
                      {sg.studentIds && <span>• Count: {sg.studentIds.length}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Metadata & Clock remaining band strip */}
        <div className="text-xs pt-2 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
          {timeRemaining ? (
            <div className="px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-xl font-bold tracking-tight text-[11px] flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeRemaining} remaining
            </div>
          ) : (
            <div className="w-2" />
          )}

          <div className="flex items-center gap-1.5">
            {isOverride && session.overrideId && (
              <span className="text-[10px] text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                Adjusted
              </span>
            )}
            {sessionCategory && !isGroupSession && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                sessionCategory === 'lab' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                sessionCategory === 'elective' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                'bg-indigo-50 text-indigo-700 border-indigo-200'
              }`}>
                {sessionCategory}
              </span>
            )}
          </div>
        </div>

        {/* Recess indicators alerts layouts strip */}
        {(isBreak || isCustom) && (
          <div className="p-2 bg-gray-50 border border-gray-100 rounded-xl text-xs font-semibold text-gray-500 flex items-center gap-1">
            {isBreak ? '⏸️ Intermission Interval active' : '⚡ Custom activity slot active'}
          </div>
        )}

      </div>
    </div>
  );
};

export default LiveSessionCard;